import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";

export default function ForgotPassword() {
  const { signedIn } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const isEmail = identifier.includes("@");
  const identifierPayload = isEmail ? { email: identifier } : { phone: identifier };

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.post("/auth/forgot-password", identifierPayload);
      setSent(true);
    } catch (e) {
      setError(e?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/reset-password", {
        ...identifierPayload,
        code,
        password,
        password_confirmation: passwordConfirmation,
      });
      signedIn(data.user);
      navigate({ admin: "/admin", business_owner: "/business" }[data.user.role] || "/account");
    } catch (e) {
      setError(e?.response?.data?.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-16">
        <h1 className="mb-2 font-montserrat text-2xl font-bold text-slate-900">Reset your password</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {isEmail
            ? `Enter the 6-digit code we emailed to ${identifier}, plus your new password.`
            : "Enter the 6-digit code we sent to the email on your account (or by SMS to this number if the account has no email), plus your new password."}{" "}
          Check your spam folder if it doesn't arrive within a minute.
        </p>
        <form onSubmit={resetPassword} className="space-y-4">
          <Input
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            maxLength={6}
            required
          />
          <Input
            type="password"
            placeholder="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="Confirm new password"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            required
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
            {loading ? "Resetting…" : "Reset password"}
          </Button>
        </form>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="mt-4 text-center text-sm text-primary w-full"
        >
          Use a different email or phone
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-16">
      <h1 className="mb-2 font-montserrat text-2xl font-bold text-slate-900">Forgot your password?</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Enter your email or phone and we'll send you a reset code.
      </p>
      <form onSubmit={requestCode} className="space-y-4">
        <Input
          placeholder="Email or phone"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          required
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading || !identifier}>
          {loading ? "Sending…" : "Send reset code"}
        </Button>
      </form>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        <Link to="/login" className="font-medium text-primary">Back to log in</Link>
      </p>
    </div>
  );
}
